(ns practitest.frontend.integration-settings.azure-form
  (:require [practitest.frontend.init :as init]
            [practitest.frontend.common.utils :as utils]
            [practitest.frontend.common.i18n :refer [get-translation format-translation]]
            [practitest.frontend.common.mixpanel :as mixpanel]
            [re-frame.core :as re-frame]
            [practitest.frontend.integration-settings.entity-mapping.events :as events]
            [practitest.frontend.integration-settings.entity-mapping.subs :as subs]
            [practitest.frontend.integration-settings.shared-form :as shared-form]
            [practitest.frontend.integration-settings.sub-tabs :as sub-tabs]
            [practitest.frontend.components.tooltip :as tooltip]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.components.selector :as selector2]
            [practitest.frontend.components2.spinners :as spinners]
            [practitest.frontend.components2.buttons :as buttons]))


(defn- get-available-type-options
  "Build dropdown options for a project's work item types, excluding types already
   selected for the other entity."
  [work-item-types current-mappings project-id entity-type other-entity-enabled?]
  (if-not work-item-types
    []
    (let [other-entity-type    (shared-form/get-other-entity-type entity-type)
          other-entity-map     (get-in current-mappings [(keyword project-id) other-entity-type])
          other-is-disabled?   (:disabled other-entity-map false)
          other-selected-types (if (or other-is-disabled? (not other-entity-enabled?))
                                 #{}
                                 (set (:ex_type other-entity-map [])))]
      (vec (remove #(contains? other-selected-types (:id %))
                   (map (fn [t] {:id (:id t) :label (:name t)}) work-item-types))))))


(defn- project-mapping-item
  [{:keys [project current-mappings entity-type other-entity-enabled?]}]
  (let [p-id-str        (name (:id project))
        work-item-types @(re-frame/subscribe [::subs/project-work-item-types])
        types-for-proj  (get work-item-types p-id-str)
        types-loading?  @(re-frame/subscribe [::subs/project-types-loading? p-id-str])
        entity-label    (shared-form/get-entity-label entity-type)
        mapping         (get current-mappings (keyword p-id-str))
        entity-map      (get mapping entity-type)
        is-disabled?    (:disabled entity-map false)
        type-options    (get-available-type-options types-for-proj current-mappings p-id-str
                                                    entity-type other-entity-enabled?)
        handle-change   (fn [types]
                          (mixpanel/track "Azure Integration Settings"
                                          {"Action" (str "Changed " entity-label " work item types")})
                          (re-frame/dispatch [::events/update-project-mapping p-id-str entity-type :ex_type types]))
        handle-toggle   (fn []
                          (mixpanel/track "Azure Integration Settings"
                                          {"Action" (str (if is-disabled? "Enable" "Disable")
                                                         " " entity-label " synchronization")})
                          (re-frame/dispatch [::events/toggle-project-entity-status p-id-str entity-type]))]
    [shared-form/project-mapping-row
     {:project-name    (:label project)
      :disabled?       is-disabled?
      :types-loading?  types-loading?
      :selected-types  (:ex_type entity-map [])
      :pt-entity       (shared-form/get-pt-entity-label entity-type)
      :type-options    type-options
      :placeholder     (format-translation (get-translation "bugtracker.integration-settings.select-item-types.placeholder"
                                                            "Select %{bugtracker} types")
                                           :bugtracker "Azure DevOps")
      :on-types-change handle-change
      :on-toggle       handle-toggle}]))



(defn- project-selector-item
  [{:keys [item] :as props}]
  (if (:has-prefix item)
    [selector2/default-selector-item props]
    [tooltip/mux-tooltip
     {:position "top"}
     [:span (get-translation "bugtracker.integration-settings.project-no-prefix.tooltip"
                             "A prefix must be configured for this project in the Azure DevOps account settings before it can be selected")]
     (fn [tooltip-props]
       [:div.selector-item-disabled-wrapper tooltip-props
        [selector2/default-selector-item props]])]))

(defn- project-selector
  [{:keys [projects-for-selector selected-projects]}]
  [:div.form-group.integration-project-selector
   [:label (format-translation (get-translation "bugtracker.integration-settings.select-project"
                                                "Select %{bugtracker} Projects to Sync")
                               :bugtracker "Azure DevOps") ":"]
   [selector2/new-select
    {:id                     "project-selector"
     :multi?                 true
     :tags?                  false
     :value                  (shared-form/filter-selected-projects projects-for-selector selected-projects)
     :items                  projects-for-selector
     :key-fn                 :id
     :label-fn               :label
     :placeholder            (str (get-translation "bugtracker.integration-settings.select-project.placeholder" "Select projects") "...")
     :selector-item-renderer project-selector-item
     :on-change              (fn [selected-set]
                               (let [selected-ids (->> selected-set
                                                       (filter :has-prefix)
                                                       (map :id)
                                                       set)]
                                 (mixpanel/track "Azure Integration Settings" {"Action" "Selected Azure Projects"})
                                 (re-frame/dispatch [::events/update-selected-projects selected-ids])))}]])



(defn azure-entity-mapping-form []
  (let [loading?              @(re-frame/subscribe [::subs/loading?])
        projects-loading?     @(re-frame/subscribe [::subs/projects-loading?])
        requirements-enabled? @(re-frame/subscribe [::subs/requirements-sync-enabled?])
        issues-enabled?       @(re-frame/subscribe [::subs/issues-sync-enabled?])
        available-projects    @(re-frame/subscribe [::subs/available-projects])
        projects-for-selector (vec (map shared-form/transform-project-for-selector available-projects))
        entity-mapping        @(re-frame/subscribe [::subs/data])
        current-mappings      (:project_mappings entity-mapping)
        selected-projects     @(re-frame/subscribe [::subs/selected-project-ids])
        allow-issue-creation? (:allow_issue_creation entity-mapping false)
        use-pt-id?            (:use_pt_id entity-mapping false)
        skip-mapping?         (:skip_mapping_in_manual_import entity-mapping false)
        selected-projects-set (shared-form/filter-selected-projects projects-for-selector selected-projects)]
    [:div.integration-form-content
     (if loading?
       [:div.loading-container
        [spinners/mux-spinner {:size :table}]]
       [:<>
        (if projects-loading?
          [:div.loading-container.projects-loading
           [spinners/mux-spinner {:size :text}]]
          [:<>
           [project-selector
            {:projects-for-selector projects-for-selector
             :selected-projects     selected-projects}]
           [shared-form/synchronization-section
            {:title                  (get-translation "bugtracker.integration-settings.requirements-synchronization.title"
                                                      "Enable Requirement Synchronization")
             :enabled?               requirements-enabled?
             :other-entity-enabled?  issues-enabled?
             :on-toggle              #(re-frame/dispatch [::events/toggle-requirements-sync %])
             :projects               selected-projects-set
             :current-mappings       current-mappings
             :entity-type            :requirements
             :bugtracker             "Azure DevOps"
             :mapping-item-component project-mapping-item}]
           [shared-form/synchronization-section
            {:title                  (get-translation "bugtracker.integration-settings.issues-synchronization.title"
                                                      "Enable Issue Synchronization")
             :enabled?               issues-enabled?
             :other-entity-enabled?  requirements-enabled?
             :on-toggle              #(re-frame/dispatch [::events/toggle-issues-sync %])
             :projects               selected-projects-set
             :current-mappings       current-mappings
             :entity-type            :issues
             :bugtracker             "Azure DevOps"
             :mapping-item-component project-mapping-item}]])
        [shared-form/general-settings-section
         {:allow-issue-creation? allow-issue-creation?
          :use-pt-id?            use-pt-id?
          :skip-mapping?         skip-mapping?
          :bugtracker            "Azure DevOps"
          :on-allow-issue-creation #(re-frame/dispatch [::events/toggle-allow-issue-creation %])
          :on-use-pt-id            #(re-frame/dispatch [::events/toggle-use-pt-id %])
          :on-skip-mapping         #(re-frame/dispatch [::events/toggle-skip-mapping-manual-import %])}]
        [shared-form/form-actions
         {:saving?   @(re-frame/subscribe [::subs/saving?])
          :dirty?    @(re-frame/subscribe [::subs/dirty?])
          :on-cancel #(re-frame/dispatch [::events/cancel-changes])
          :on-save   #(re-frame/dispatch [::events/save-settings])}]])]))

(defn azure-integration-form-root [_]
  (fn [_]
    [:<>
     [sub-tabs/sub-tabs @(re-frame/subscribe [::subs/sub-tabs-props])]
     [azure-entity-mapping-form]]))

(defn ^:export init [webcomponent options]
  (init/init! {:options       options
               :init-event-fn (fn [config]
                                [[::events/initialize-db config]
                                 [::events/fetch-entity-mapping]
                                 [::events/fetch-available-projects]])
               :webcomponent  webcomponent
               :root-view     azure-integration-form-root}))

(defn ^:dev/after-load remount []
  (init/remount-roots "azure-integration-form-component"
                      azure-integration-form-root))

(utils/register-webcomponent! "azure-integration-form-component")
